import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { CircularProgress, Typography } from '@material-ui/core';
import SystemUpdateAltIcon from '@material-ui/icons/SystemUpdateAlt';
import AssistantPhotoIcon from '@material-ui/icons/AssistantPhoto';
import CheckCircleOutlineIcon from '@material-ui/icons/CheckCircleOutline';
import { useStyles } from './Styles';
import ReviewDocumentWindow from './ReviewDocumentWindow';
import { fetchPDBatchDocumentsAPI, downloadPDCustomFileAPI } from './batchDocumentsAPI';
import { formatDisplayDate, padCount } from './formatters';

/**
 * Batch-level "Documents" tab (Documents Tab - Functional Specs).
 *
 * Unlike the other batch tabs this is NOT a flat table, so it does not go
 * through GenericTable: the FSD organizes it into numbered document-category
 * sections down a vertical progress rail (§3), and within a section rows are
 * grouped under a shared Record ID with each document as a sub-row
 * (BR-DOC-DEV-01). ProjectTabs renders this component in place of GenericTable
 * for the Documents tab and skips its normal tab-data fetch, since the shape of
 * the data has nothing in common with the other tabs' { columns, rows }.
 *
 * Column set is fixed by the FSD (§3): Record ID / Download Document / Review
 * Digitalized Document / Upload Date. A campaign's column configuration can
 * switch any of them off — same contract as every other tab, where no config
 * means "show all columns".
 *
 * Section ORDER comes from the server already sorted by recency (FSD §7), so the
 * numbers on the rail are simply the array index + 1. Do not re-sort here.
 */

// FSD §3 column set, in FSD order. `key` is what a campaign's column
// configuration is matched against (see visibleColumns below).
const COLUMNS = [
  { key: 'recordId', label: null },        // label comes from the section (Deviation ID / CC ID / ...)
  { key: 'download', label: 'Download Document' },
  { key: 'review', label: 'Review Digitalized Document' },
  { key: 'uploadDate', label: 'Upload Date' },
];

// Config column names (from the campaignManagementColumns seed) -> our column
// keys. The seed's own wording is matched first; the older Documents seed
// (Document Type / File Name / Upload Date) is mapped too so a campaign saved
// before this tab was redesigned still resolves to something sensible rather
// than hiding every column.
const CONFIG_NAME_TO_KEY = {
  'record id': 'recordId',
  'deviation id': 'recordId',
  'cc id': 'recordId',
  'download document': 'download',
  'file name': 'download',
  'document type': 'download',
  'review digitalized document': 'review',
  'review digitized document': 'review',
  'upload date': 'uploadDate',
};

const norm = (v) => String(v == null ? '' : v).trim().toLowerCase();

export default function DocumentsTab({
  campaignId,
  batchNumber,
  columnConfig,
  refreshToken,
  onSectionsLoaded,
}) {
  const classes = useStyles();

  const [loading, setLoading] = useState(false);
  const [sections, setSections] = useState([]);
  const [failed, setFailed] = useState(false);
  // Per-document in-flight download, so the clicked file name (and only it)
  // shows as busy — same behaviour as the Document Repository's row download.
  const [downloadingIds, setDownloadingIds] = useState(() => new Set());
  const [reviewTarget, setReviewTarget] = useState(null);

  // `onSectionsLoaded` is called on every settled load (including with []) so
  // the parent's "Download Report" never exports a stale batch's documents.
  // Read through a ref so an unstable parent callback cannot re-trigger the
  // fetch — the effect's dependencies stay the scope and the refresh token.
  const onSectionsLoadedRef = useRef(onSectionsLoaded);
  useEffect(() => {
    onSectionsLoadedRef.current = onSectionsLoaded;
  }, [onSectionsLoaded]);

  useEffect(() => {
    if (!campaignId || !batchNumber) {
      setSections([]);
      setFailed(false);
      setLoading(false);
      if (onSectionsLoadedRef.current) onSectionsLoadedRef.current([]);
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    setFailed(false);
    (async () => {
      const res = await fetchPDBatchDocumentsAPI({ campaignID: campaignId, batchNumber });
      if (cancelled) return;
      // A failed request is presented as an empty tab, not a red error — the
      // same convention ProjectTabs applies to every other tab, because the
      // difference between "no documents" and "endpoint unavailable" means
      // nothing to the user. The console.error in the API module keeps the
      // real failure for diagnosis.
      const loaded = res?.data?.sections || [];
      setSections(loaded);
      setFailed(!res?.status);
      setLoading(false);
      if (onSectionsLoadedRef.current) onSectionsLoadedRef.current(loaded);
    })();
    return () => {
      cancelled = true;
    };
  }, [campaignId, batchNumber, refreshToken]);

  // Which of the four FSD columns to render. No config for this tab -> all of
  // them, matching getVisibleColumns' fallback in ProjectTabs.
  const visibleColumns = useMemo(() => {
    if (!Array.isArray(columnConfig) || columnConfig.length === 0) return COLUMNS;
    const active = new Set();
    columnConfig.forEach((entry) => {
      const key = CONFIG_NAME_TO_KEY[norm(entry?.columnName)];
      if (key) active.add(key);
    });
    // A config that resolves to nothing we know about would blank the tab
    // entirely, which is never what the user meant — fall back to all columns.
    if (active.size === 0) return COLUMNS;
    return COLUMNS.filter((c) => active.has(c.key));
  }, [columnConfig]);

  const handleDownload = useCallback(async (doc) => {
    if (doc?.collabrixPDCustomFileID == null) return;
    setDownloadingIds((prev) => new Set(prev).add(doc.id));
    try {
      const res = await downloadPDCustomFileAPI(doc.collabrixPDCustomFileID);
      if (!res?.status || !res?.fileData?.data) {
        console.error('[DocumentsTab] Download failed for document:', doc.id);
        return;
      }
      const a = window.document.createElement('a');
      a.href = URL.createObjectURL(
        new Blob([new Uint8Array(res.fileData.data)], { type: 'application/octet-stream' })
      );
      a.download = res.fileName || doc.fileName || 'document';
      window.document.body.appendChild(a);
      a.click();
      window.document.body.removeChild(a);
      URL.revokeObjectURL(a.href);
    } finally {
      setDownloadingIds((prev) => {
        const next = new Set(prev);
        next.delete(doc.id);
        return next;
      });
    }
  }, []);

  if (loading) {
    return (
      <div className={classes.loading}>
        <CircularProgress size={22} />
        <Typography variant="body2" style={{ color: '#666' }}>
          Loading Documents…
        </Typography>
      </div>
    );
  }

  if (sections.length === 0) {
    return (
      <div className={classes.empty}>
        {failed || !campaignId || !batchNumber
          ? 'No data available.'
          : 'No documents available for this batch.'}
      </div>
    );
  }

  const renderCell = (columnKey, doc) => {
    switch (columnKey) {
      case 'download': {
        const busy = downloadingIds.has(doc.id);
        const downloadable = doc.collabrixPDCustomFileID != null;
        return (
          <div className={classes.downloadCell}>
            {busy ? (
              <CircularProgress size={16} />
            ) : (
              <SystemUpdateAltIcon className={classes.downloadIcon} />
            )}
            <button
              type="button"
              className={classes.fileLink}
              onClick={() => handleDownload(doc)}
              disabled={busy || !downloadable}
            >
              {doc.fileName}
            </button>
          </div>
        );
      }

      case 'review': {
        // FSD §4 Condition / BR-DOC-REVIEW-01: the control appears only on rows
        // that have been through digitalization and produced extractable
        // fields. A row without them shows nothing at all — matching the FSD's
        // own example, where the Investigation Report rows carry no review status.
        if (!doc.digitized) return null;
        const flagged = doc.fieldsRequiringReview > 0;
        return (
          <button
            type="button"
            className={classes.reviewButton}
            onClick={() =>
              setReviewTarget({ id: doc.id, label: doc.documentType || doc.fileName })
            }
          >
            {flagged ? (
              <AssistantPhotoIcon className={classes.reviewIconFlagged} />
            ) : (
              <CheckCircleOutlineIcon className={classes.reviewIconClear} />
            )}
            <span
              className={`${classes.reviewCounts} ${flagged ? '' : classes.reviewCountsMuted}`}
            >
              {`Fields that require review - ${padCount(doc.fieldsRequiringReview)}`}
              <br />
              {`Fields reviewed - ${doc.fieldsReviewed}`}
            </span>
          </button>
        );
      }

      case 'uploadDate':
        return formatDisplayDate(doc.uploadDate);

      default:
        return null;
    }
  };

  return (
    <div className={classes.root}>
      {sections.map((section, sectionIndex) => {
        const isLastSection = sectionIndex === sections.length - 1;
        return (
          <div className={classes.section} key={section.key || section.title}>
            {/* Vertical numbered progress indicator (FSD §3). The number is the
                section's position in the server's recency order. */}
            <div className={classes.rail}>
              <div className={classes.railNumber}>{sectionIndex + 1}</div>
              {!isLastSection && <div className={classes.railLine} />}
            </div>

            <div className={classes.sectionBody}>
              <Typography className={classes.sectionTitle}>{section.title}</Typography>

              <div className={classes.tableWrap}>
                <table className={classes.table}>
                  <thead>
                    <tr>
                      {visibleColumns.map((column) => (
                        <th className={classes.th} key={column.key}>
                          {/* The Record ID header is the section's own label
                              (Deviation ID / CC ID / ...), per FSD §3. */}
                          {column.key === 'recordId'
                            ? section.recordIdLabel || 'Record ID'
                            : column.label}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {(section.groups || []).map((group, groupIndex) => {
                      const documents = group.documents || [];
                      const isLastGroup = groupIndex === (section.groups || []).length - 1;
                      return documents.map((doc, docIndex) => {
                        const isLastInGroup = docIndex === documents.length - 1;
                        // Only the final row of the final group drops its
                        // bottom border (the table's own border closes it off).
                        const bottomClass = isLastGroup && isLastInGroup
                          ? classes.lastRowCell
                          : isLastInGroup
                            ? ''
                            : classes.innerRowCell;
                        return (
                          <tr key={doc.id || `${group.recordId}-${docIndex}`}>
                            {visibleColumns.map((column) => {
                              if (column.key === 'recordId') {
                                // Shown once per group, spanning its sub-rows
                                // (BR-DOC-DEV-01).
                                if (docIndex !== 0) return null;
                                return (
                                  <td
                                    key={column.key}
                                    className={`${classes.recordIdCell} ${
                                      isLastGroup ? classes.lastRowCell : ''
                                    }`}
                                    rowSpan={documents.length}
                                  >
                                    {group.recordId}
                                  </td>
                                );
                              }
                              return (
                                <td
                                  key={column.key}
                                  className={`${classes.td} ${bottomClass}`}
                                >
                                  {renderCell(column.key, doc)}
                                </td>
                              );
                            })}
                          </tr>
                        );
                      });
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        );
      })}

      {/* One shared review window for every section (BR-DOC-REVIEW-01), scoped
          to the single document whose icon was clicked (FSD §4 Behavior). */}
      <ReviewDocumentWindow
        open={!!reviewTarget}
        documentId={reviewTarget?.id || null}
        documentLabel={reviewTarget?.label || ''}
        onClose={() => setReviewTarget(null)}
      />
    </div>
  );
}

DocumentsTab.propTypes = {
  // Business Campaign ID (e.g. "CMPO04BRS729") — NOT collabrixCampaignID.
  campaignId: PropTypes.string,
  // CMO batch number (e.g. "DBSQA25001").
  batchNumber: PropTypes.string,
  // The campaign's active column entries for this tab ([{ columnName,
  // newColumnName }]), or null/[] when the campaign has no configuration.
  columnConfig: PropTypes.array,
  // Changing this re-fetches (bumped when the user re-selects the tab).
  refreshToken: PropTypes.any,
  // Called with the loaded sections after every settled fetch, so the parent's
  // "Download Report" can export this tab.
  onSectionsLoaded: PropTypes.func,
};

DocumentsTab.defaultProps = {
  campaignId: null,
  batchNumber: null,
  columnConfig: null,
  refreshToken: null,
  onSectionsLoaded: null,
};
